import React, { ReactElement, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { DragDropContext, DropResult, Droppable, Draggable } from "react-beautiful-dnd";
import { QualityHubTasksListHeader } from "../QualityHubTasksListHeader";
import { QualityHubTasksListGroup } from "../QualityHubTasksListGroup";
import {
    QualityHubTasksListProps,
    QualityHubTasksListState, 
    QualityHubTasksListStateProps, 
    QualityHubTasksListLocalChanges,
} from './QualityHubTasksList.types';
import { QualityHubTaskDetailsDrawer } from "src/components/QualityHubTaskDetails";
import { QualityHubActionFormDrawer } from "src/components/QualityHubActionForm";
import { QualityHubTasksListItem } from "../QualityHubTasksListItem";
import { Conditional } from "src/components/Conditional";
import { SpintrTypes } from "src/typings";
import { isDateInDaysOfWeek } from "src/utils";
import {
    QualityHubTasksListContextProvider,
    QualityHubTasksListContextType,
} from "../QualityHubTasksListContext";
import QualityHubTasksListEditBar from "../QualityHubTasksListEditBar/QualityHubTasksListEditBar";
import { getFilteredTasksForGroup } from "../QualityHubTasksList.utils";
import {
    queryMyTaskLists,
    taskListItemCreated,
    taskListItemDeleted,
    updateTaskListItemOverrides,
    taskListGroupCreated,
    setTaskListGroupOverrides,
} from "src/redux/actions/qualityHubActions";
import { QualityHubTaskFormDrawer } from "src/components/QualityHubTaskForm";
import { createTaskListItemAsync } from "src/api";
import { generateUniqueNumberId } from "src/utils/uniqueId";
import { useHistory, useLocation } from "react-router";
import classNames from "classnames";
import { QualityHubTaskGroupFormDrawer } from "src/components/QualityHubTaskGroupForm";
import { localize } from "src/l10n";

/**
 * Renders the QualityHubTasksList component.
 *
 * @param {QualityHubTasksListProps} props - The component props
 * @returns {ReactElement | null} The rendered component
 */
function QualityHubTasksList({
    listId,
    listMode,
}: QualityHubTasksListProps): ReactElement | null {
    const rootEl = useRef<HTMLDivElement>(null);
    const [context, setContext] = useState<QualityHubTasksListContextType>({
        advanceFrom: () => {},
        backwardFrom: () => {},
        createAction: () => {},
        setExpandedItemId: () => {},
    });

    const [state, setState] = useState<QualityHubTasksListState>({
        drawer: "none",
    });

    // Track if any item is being dragged
    const [anyItemDragging, setAnyItemDragging] = useState(false);

    const [hideDisabledItems, setHideDisabledItems] = useState(false);
    const [
        pendingTaskChanges,
        setPendingTaskChanges,
    ] = useState<QualityHubTasksListLocalChanges>({
        groups: new Map(),
        tasks: new Map(),
    });

    const {
        taskList,
        taskLists,
        smallViewMode,
    } = useSelector<Spintr.AppState, QualityHubTasksListStateProps>(
        (appState) => ({
            taskList: appState.qualityHub.listsById[listId],
            taskLists: appState.qualityHub.listsById,
            smallViewMode: appState.ui.isSmallViewMode,
        }),
    );

    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();

    const editMode = (
        listMode === SpintrTypes.QualityHubTasksListMode.Customization ||
        listMode === SpintrTypes.QualityHubTasksListMode.Creation);

    const onItemClick = useCallback(
        (item: Spintr.TaskListTask) => {
            if (listMode === SpintrTypes.QualityHubTasksListMode.Customization) {
                if (!item.allowOverride) {
                    return;
                }
            }

            setState((prevState) => ({
                ...prevState,
                selectedTask: item,
                drawer: editMode ? "taskForm" : "details",
            }));
        }, [setState, listMode]);

    const onDetailsDismissing = useCallback(
        () => setState((prevState) => prevState.drawer !== "details" ? prevState : ({
            ...prevState,
            selectedTask: undefined,
            drawer: "none",
        })),
        [setState]);

    const onCreateActionDismissing = useCallback(
        (saved: boolean = false) => {
            state.actionCallback?.(saved);

            setState((prevState) => ({
                ...prevState,
                selectedAction: undefined,
                selectedTask: undefined,
                drawer: "none",
                actionCallback: undefined,
            }));
        },
        [setState, state.actionCallback],
    );

    const onCreateActionClick = useCallback(
        (task: Spintr.TaskListTask) => setState((prevState) => ({
            ...prevState,
            selectedTask: task,
            drawer: "actionForm",
        })),
        [setState],
    );

    const onCreateTaskDismissing = useCallback(
        () => {
            setState((prevState) => ({
                ...prevState,
                drawer: "none",
                selectedTask: undefined,
            }));
        },
        [setState],
    );

     const onLocalSave = useCallback(
        (task) => {
            if (!task.id) {
                dispatch(
                    taskListItemCreated({
                        ...task,
                        id: 0 - generateUniqueNumberId(),
                        createdOn: new Date(),
                        tags: [],
                        sortOrder: (taskList?.tasks?.reduce((max, t) => Math.max(max, t.sortOrder), 0) ?? 0) + 1,
                    })
                );
            } else {
                setPendingTaskChanges((prev) => {
                    const newMap = new Map(prev.tasks);
                    const existingChanges = newMap.get(task.id) || {};
                    newMap.set(task.id, { ...existingChanges, ...task });
                    return { ...prev, tasks: newMap };
                });
            }

            if (!state.hasUnsavedChanges) {
                setState((prevState) => ({ ...prevState, hasUnsavedChanges: true }));
            }
        },
        [taskList, state.hasUnsavedChanges, setState, state]
    );

    const saveGroupLocally = useCallback(
        (groupModel) => {
            if (!groupModel.id) {
                dispatch(taskListGroupCreated({
                    ...groupModel,
                    id: 0 - generateUniqueNumberId(),
                    sortOrder: (taskList?.groups?.reduce((max, g) => Math.max(max, g.sortOrder), 0) ?? 0) + 1,
                }));
            } else {
                setPendingTaskChanges((prev) => {
                    const newMap = new Map(prev.groups);
                    const existingChanges = newMap.get(groupModel.id) || {};
                    newMap.set(groupModel.id, { ...existingChanges, ...groupModel });
                    return { ...prev, groups: newMap };
                });
            }

            setState((prevState) => ({ ...prevState, hasUnsavedChanges: true }));
        },
        [taskList?.groups]);

    const clearChanges = () => {
        setPendingTaskChanges({
            groups: new Map(),
            tasks: new Map(),
        });

        // search all lists since taskList may have already changed
        const newItems = Object.values(taskLists)
            .flatMap(list => list.tasks ?? [])
            .filter(task => task && task.id < 0);

        for (const newItem of newItems) {
            dispatch(taskListItemDeleted(newItem.id, newItem.listId));
        }

        setState((prevState) => ({ ...prevState, hasUnsavedChanges: false }));
    }

    const unpublishedTasks = useMemo(() => 
        (taskList?.tasks ?? [])
            .filter(task => task.contentStatus === SpintrTypes.ContentStatus.Draft),
        [taskList?.tasks]);

    // For compatibility with existing code that needs raw groupless tasks
    const grouplessTasks = useMemo(
        () => taskList?.tasks?.filter((task) => !task.groupId) || [],
        [taskList]);

    const filteredGrouplessTasks = useMemo(() => {
        return getFilteredTasksForGroup(null, taskList, pendingTaskChanges, hideDisabledItems, listMode);
    }, [taskList, pendingTaskChanges, hideDisabledItems, editMode]);

    const groups = useMemo(() => (taskList?.groups || [])
        .map((g) => {
            if (listMode !== SpintrTypes.QualityHubTasksListMode.Customization) {
                return g;
            }

            const pendingChanges = pendingTaskChanges.groups.get(g.id);
            if (!pendingChanges) {
                return g;
            }

            return {
                ...g,
                ...pendingChanges,
            };

        })
        .sort((a, b) => {
            if (a.sortOrder !== b.sortOrder) {
                return a.sortOrder - b.sortOrder;
            }

            if (a.startTime && !b.startTime) {
                return 1;
            }

            if (!a.startTime && b.startTime) {
                return -1;
            }

            if (a.startTime && b.startTime) {
                return a.startTime > b.startTime ? 1 : -1;
            }

            if (a.endTime && b.endTime) {
                return a.endTime > b.endTime ? 1 : -1
            };

            return a.id - b.id;
        }), [taskList?.groups, pendingTaskChanges?.groups]);

    useEffect(function _QualityHubTasksListSetExpanded() {
        if (state.expandedId) {
            return;
        }

        const now = new Date();
        const allTasks = groups
            .flatMap((g) => (taskList?.tasks ?? []).filter((t) => t.groupId === g.id))
            .concat(grouplessTasks)
            .filter((task) => task.contentStatus === SpintrTypes.ContentStatus.Published)
            .filter((task) => !task.daysOfWeek || isDateInDaysOfWeek(now, task.daysOfWeek))
            .filter((task) => {
                const status = taskList?.statuses?.find((s) => s.itemId === task.id);
            
                return !status || (status.status !== SpintrTypes.TaskListItemStatus.Completed &&
                                   status.status !== SpintrTypes.TaskListItemStatus.Failed);
            })

        if (allTasks.length === 0) {
            return;
        }

        // TODO: Change this to first incomplete task in the list

        setState((prevState) => ({ ...prevState, expandedId: allTasks[0].id }));
    }, [groups, grouplessTasks, state.expandedId, taskList?.tasks]);

    const onTaskChange = useCallback((item: Spintr.TaskListTask, changes: Partial<Spintr.TaskListTask>) => {
        setPendingTaskChanges((prev) => {
            const newMap = new Map(prev.tasks);
            const existingChanges = newMap.get(item.id) || {};
            newMap.set(item.id, { ...item, ...existingChanges, ...changes });
            return { ...prev, tasks: newMap };
        });

        if (!state.hasUnsavedChanges) {
            setState((prevState) => ({ ...prevState, hasUnsavedChanges: true }));
        }
    }, [state.hasUnsavedChanges, setState]);

    const renderGroup = useCallback((group: Spintr.TaskListGroup) => {        
        const now = new Date();
        const tasks = (taskList?.tasks ?? [])
                .map((task) => {
                    const pendingChanges = pendingTaskChanges.tasks.get(task.id);
                    if (pendingChanges) {
                        return {
                            ...task,
                            ...pendingChanges,
                        };
                    }
                    return task;
                })
                .filter(
                    (task) =>
                        task.groupId === group.id &&
                        (!task.daysOfWeek || isDateInDaysOfWeek(now, task.daysOfWeek)) &&
                        ((editMode && !hideDisabledItems) || task.contentStatus === SpintrTypes.ContentStatus.Published)
                );

        if (tasks.length === 0 && listMode !== SpintrTypes.QualityHubTasksListMode.Creation) {
            return null;
        }

        return (
            <li key={group.id} className="QualityHubTasksList-group">
                <QualityHubTasksListGroup expandedItemId={state.expandedId}
                                          group={group}
                                          onEditGroupClick={editMode ? onEditGroupClicked : undefined}
                                          onCreateTaskClick={editMode
                                            ? handleAddNewTask
                                            : undefined}
                                          onCreateActionClick={onCreateActionClick}
                                          onItemClick={onItemClick}
                                          taskList={taskList}
                                          listMode={listMode}
                                          hideDisabledItems={hideDisabledItems}
                                          pendingTaskChanges={pendingTaskChanges.tasks}
                                          onTaskChange={onTaskChange}
                                          isAnyItemDragging={anyItemDragging} />
            </li>
        );
    }, [taskList?.tasks, state.expandedId, onItemClick, onCreateActionClick,
        taskList, editMode, hideDisabledItems, pendingTaskChanges, 
        onTaskChange, anyItemDragging, listMode]);

    const onEditClicked = useCallback(
        (task: Spintr.TaskListTask) => setState((prevState) => ({
            ...prevState,
            selectedAction: task,
            selectedTask: task,
            drawer: "actionForm",
        })), []);

    const onDragStart = useCallback(() => {
        setAnyItemDragging(true);
    }, []);

    const onDragEnd = useCallback((result: DropResult) => {
        setAnyItemDragging(false);
        // If dropped outside a droppable area, do nothing
        if (!result.destination) {
            return;
        }

        const { source, destination, draggableId } = result;

        // If dropped in the same position, do nothing
        if (source.droppableId === destination.droppableId && source.index === destination.index) {
            return;
        }

        const itemId = parseInt(draggableId, 10);
        const sourceGroupId = source.droppableId === "groupless" ? null : parseInt(source.droppableId, 10);
        const destinationGroupId = destination.droppableId === "groupless" ? null : parseInt(destination.droppableId, 10);

        const task = taskList?.tasks?.find(t => t.id === itemId);
        if (!task) {
            return;
        }

        const allChanges = new Map(pendingTaskChanges.tasks);

        if (sourceGroupId !== destinationGroupId) {
            // Moving between different groups
            const sourceFilteredTasks = getFilteredTasksForGroup(sourceGroupId, taskList, pendingTaskChanges, hideDisabledItems, listMode);
            const destinationFilteredTasks = getFilteredTasksForGroup(destinationGroupId, taskList, pendingTaskChanges, hideDisabledItems, listMode);

            // Remove task from source group and update sortOrder for remaining tasks
            const sourceReorderedTasks = sourceFilteredTasks.filter(t => t.id !== itemId);
            sourceReorderedTasks.forEach((task, index) => {
                const existingChanges = allChanges.get(task.id) || {};
                allChanges.set(task.id, {
                    ...task,
                    ...existingChanges,
                    sortOrder: index
                });
            });

            // Add task to destination group and update sortOrder for all tasks in destination
            const destinationReorderedTasks = [...destinationFilteredTasks];
            const draggedTask = {
                ...task,
                ...allChanges.get(task.id),
                groupId: destinationGroupId
            };
            destinationReorderedTasks.splice(destination.index, 0, draggedTask);

            destinationReorderedTasks.forEach((task, index) => {
                const existingChanges = allChanges.get(task.id) || {};
                allChanges.set(task.id, {
                    ...task,
                    ...existingChanges,
                    groupId: destinationGroupId,
                    sortOrder: index
                });
            });
        } else {
            // Moving within the same group
            const sourceFilteredTasks = getFilteredTasksForGroup(sourceGroupId, taskList, pendingTaskChanges, hideDisabledItems, listMode);

            const reorderedTasks = [...sourceFilteredTasks];
            const [draggedTask] = reorderedTasks.splice(source.index, 1);
            reorderedTasks.splice(destination.index, 0, draggedTask);

            // Update sortOrder for all tasks in the reordered array
            reorderedTasks.forEach((task, index) => {
                const existingChanges = allChanges.get(task.id) || {};
                allChanges.set(task.id, {
                    ...task,
                    ...existingChanges,
                    sortOrder: index
                });
            });
        }

        setPendingTaskChanges((prev) => ({
            ...prev,
            tasks: allChanges
        }));

        if (!state.hasUnsavedChanges) {
            setState((prevState) => ({ ...prevState, hasUnsavedChanges: true }));
        }
    }, [taskList, pendingTaskChanges, hideDisabledItems, editMode, state.hasUnsavedChanges, setState]);

    const onHideDisabledItemsChange = useCallback((hideDisabled: boolean) => {
        setHideDisabledItems(hideDisabled);
    }, []);

    const handleEditCancel = useCallback(() => {
        history.push(location.pathname.replace(/\/customize$/, ''));
    }, [history, location.pathname, taskList]);

    const handleEditSave = useCallback(async () => {
        const newItems = taskList.tasks.filter(task => task.id < 0);

        for (const newItem of newItems) {
            const newItemData = await createTaskListItemAsync({
                ...newItem,
                tagIds: [],
                createForUnit: true,
            });

            if (newItemData.id) {
                pendingTaskChanges.tasks.set(
                    newItemData.id,
                    pendingTaskChanges.tasks.get(newItem.id));
                pendingTaskChanges.tasks.delete(newItem.id);
            }
        }

        const groupChanges: Record<string, Partial<Spintr.TaskListGroup>> = {};
        pendingTaskChanges.groups.forEach((changes, groupId) => {
            groupChanges[groupId.toString()] = changes;
        });

        const changesObject: Record<string, Partial<Spintr.TaskListTask>> = {};
        pendingTaskChanges.tasks.forEach((changes, itemId) => {
            changesObject[itemId.toString()] = changes;
        });

        try {
            await (dispatch(setTaskListGroupOverrides(listId, groupChanges)) as any);
            await (dispatch(updateTaskListItemOverrides(listId, changesObject)) as any);

            clearChanges();
            dispatch(queryMyTaskLists());

            history.push(location.pathname.replace(/\/customize$/, ''));
        } catch (error) {
            console.error('Failed to save changes:', error);
        }
    }, [pendingTaskChanges, dispatch, listId, taskList, history, location.pathname]);

    const handleAddNewTask = useCallback(
        (groupId?: number) => setState((prevState) => ({
            ...prevState,
            drawer: "taskForm",
            selectedGroup: groups.find((g) => g.id === groupId)
        })), [listId, setState, groups]);

    const onGroupFormDismissing = useCallback(
        () => setState((prevState) => ({
            ...prevState,
            drawer: "none",
            selectedGroup: undefined,
        })),
        [setState]);

    const handleAddNewGroupClicked = useCallback(
        () => setState((prevState) => ({
            ...prevState,
            drawer: "groupForm",
        })),
        [setState]);

    const onEditGroupClicked = useCallback((group: Spintr.TaskListGroup) => {
        if (!group.allowOverride && listMode === SpintrTypes.QualityHubTasksListMode.Customization) {
            return;
        }

        setState((prevState) => ({
            ...prevState,
            drawer: "groupForm",
            selectedGroup: group,
        }));
    }, [setState, listMode]);

    useEffect(() => {
        if (listMode !== SpintrTypes.QualityHubTasksListMode.Customization) {
           clearChanges();
        }
    }, [listMode]);

    useEffect(clearChanges, [listId]);

    useEffect(function QualityHubTasksListSetContext() {
        setContext((ctx) => ({
            ...ctx,
            advanceFrom: (itemId: number, immediateNeighbour: boolean = false) => {
                setState((prevState) => {
                    const now = new Date();
                    const allTasks = groups
                        .flatMap((g) => (taskList.tasks ?? []).filter((t) => t.groupId === g.id))
                        .concat(grouplessTasks)
                        .filter((task) => !task.daysOfWeek || isDateInDaysOfWeek(now, task.daysOfWeek));

                    const currentIndex = allTasks.findIndex(
                        (task) => task.id === itemId,
                    );

                    if (immediateNeighbour) {
                        return {
                            ...prevState,
                            expandedId: currentIndex + 1 >= allTasks.length
                                ? allTasks[0].id // wrap around
                                : allTasks[currentIndex + 1].id,
                        }
                    }

                    let nextTask: Spintr.TaskListTask | undefined;
                    for (let i = currentIndex + 1; i < allTasks.length; i++) {
                        const task = allTasks[i];
                        if (!task) {
                            continue;
                        }

                        const status = taskList?.statuses?.find((s) => s.itemId === task.id);

                        const isCompleted = status && (
                            status.status === SpintrTypes.TaskListItemStatus.Completed ||
                            status.status === SpintrTypes.TaskListItemStatus.Failed
                        );

                        if (isCompleted) {
                            continue;
                        }

                        nextTask = task;
                        break;
                    }

                    return {
                        ...prevState,
                        expandedId: nextTask?.id,
                    };
                });
            },

            backwardFrom: (itemId: number) => {
                setState((prevState) => {
                    const now = new Date();
                    const allTasks = groups
                        .flatMap((g) => (taskList.tasks ?? []).filter((t) => t.groupId === g.id))
                        .concat(grouplessTasks)
                        .filter((task) => !task.daysOfWeek || isDateInDaysOfWeek(now, task.daysOfWeek));

                    const currentIndex = allTasks.findIndex((task) => task.id === itemId);
                    return {
                        ...prevState,
                        expandedId: currentIndex > 0
                            ? allTasks[currentIndex - 1].id
                            : allTasks[allTasks.length - 1].id, // wrap around
                    };
                });
            },

            createAction: (task, action) => setState((prevState) => ({
                ...prevState,
                selectedTask: task,
                drawer: "actionForm",
                actionCallback: action,
            })),

            setExpandedItemId: (itemId: number | undefined) => setState((prevState) => ({
                ...prevState,
                expandedId: itemId,
            })),
        }));
    }, [groups, grouplessTasks, taskList?.tasks]);

    useEffect(function scrollToExpandedItem() {
        if (!state.expandedId || !rootEl.current || !smallViewMode) {
            return;
        }

        const itemEl = rootEl.current.querySelector(
            `.QualityHubTasksListItem[data-item-id="${state.expandedId}"]`
        ) as HTMLElement | null;

        if (!itemEl) {
            return;
        }

        // Find the previous sibling element
        let previousSiblingEl = itemEl.closest('li')?.previousElementSibling?.querySelector('.QualityHubTasksListItem') as HTMLElement | null;
        
        const elementToScroll = previousSiblingEl || itemEl;
        
        // Find the actual scrolling container by walking up the DOM tree
        const findScrollContainer = (element: HTMLElement): HTMLElement | null => {
            let current = element.parentElement;
            while (current && current !== document.body) {
                const computedStyle = window.getComputedStyle(current);
                const hasVerticalScroll = computedStyle.overflowY === 'auto' || 
                                        computedStyle.overflowY === 'scroll' ||
                                        computedStyle.overflow === 'auto' ||
                                        computedStyle.overflow === 'scroll';
                
                if (hasVerticalScroll && current.scrollHeight > current.clientHeight) {
                    return current;
                }
                current = current.parentElement;
            }
            return null;
        };

        const scrollContainer = findScrollContainer(rootEl.current);
        let headerOffset = 64;

        if (!previousSiblingEl) {
            previousSiblingEl = itemEl
                ?.closest("div.QualityHubTasksListGroup")
                ?.querySelector(".QualityHubTasksListGroup-header") as HTMLElement | null;
        }

        if (previousSiblingEl) {
            const elHeight = previousSiblingEl.getBoundingClientRect().height;
            const extraOffset = previousSiblingEl.classList.contains("QualityHubTasksListGroup-header")
                ? elHeight
                : Math.min(40, elHeight);
            headerOffset += extraOffset;
        } 

        if (scrollContainer) {
            // Calculate the target scroll position manually for smooth single scroll
            const containerRect = scrollContainer.getBoundingClientRect();
            const elementRect = elementToScroll.getBoundingClientRect();
            const relativeTop = elementRect.top - containerRect.top;
            const targetScrollTop = scrollContainer.scrollTop + relativeTop - headerOffset;
            
            scrollContainer.scrollTo({
                top: Math.max(0, targetScrollTop),
                behavior: "smooth"
            });
        } else if (rootEl.current && rootEl.current.scrollHeight > rootEl.current.clientHeight) {
            // Calculate for root element scrolling
            const containerRect = rootEl.current.getBoundingClientRect();
            const elementRect = elementToScroll.getBoundingClientRect();
            const relativeTop = elementRect.top - containerRect.top;
            const targetScrollTop = rootEl.current.scrollTop + relativeTop - headerOffset;
            
            rootEl.current.scrollTo({
                top: Math.max(0, targetScrollTop),
                behavior: "smooth"
            });
        } else {
            // Calculate for window scrolling
            const elementRect = elementToScroll.getBoundingClientRect();
            const targetScrollTop = window.pageYOffset + elementRect.top - headerOffset;
            
            window.scrollTo({
                top: Math.max(0, targetScrollTop),
                behavior: "smooth"
            });
        }
    }, [state.expandedId, rootEl, smallViewMode]);

    const showUnpublishedTasks = !editMode && unpublishedTasks.length > 0 && taskList?.listType === SpintrTypes.TaskListType.ActionList;
    const unpublishedTasksGroup = useMemo<Spintr.TaskListGroup>(() => ({
        id: -1,
        listId: taskList?.id || 0,
        name: localize("UNPUBLISHED_CONTENT"),
        sortOrder: Number.MAX_SAFE_INTEGER,
        allowOverride: false,
        createdOn: new Date(),
    }), [taskList]);

    if (!taskList) {
        return null;
    }

    return (
        <QualityHubTasksListContextProvider value={context}>
            <div className="QualityHubTasksList" ref={rootEl}>
                <Conditional condition={editMode}>
                    <QualityHubTasksListEditBar
                        hideDisabledItems={hideDisabledItems}
                        onAddNewGroupClicked={
                            listMode === SpintrTypes.QualityHubTasksListMode.Creation
                                ? handleAddNewGroupClicked
                                : undefined}
                        onAddNewTask={handleAddNewTask}
                        onCancel={
                            listMode === SpintrTypes.QualityHubTasksListMode.Creation
                                ? undefined
                                : handleEditCancel}
                        onSave={
                            listMode === SpintrTypes.QualityHubTasksListMode.Creation
                                ? undefined
                                : handleEditSave}
                        onHideDisabledItemsChange={
                            listMode === SpintrTypes.QualityHubTasksListMode.Creation
                                ? undefined
                                : onHideDisabledItemsChange}
                    />
                </Conditional>
                <QualityHubTasksListHeader smallViewMode={smallViewMode}
                                           title={taskList?.name} />
                <Conditional condition={editMode}>
                    <form className={classNames({"form-dirty": state.hasUnsavedChanges})}>
                        <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
                            <ol className="QualityHubTasksList-groups">
                                {groups.map(renderGroup)}
                                <Conditional condition={filteredGrouplessTasks.length > 0}>
                                    <li className="QualityHubTasksList-group">
                                        <Droppable droppableId="groupless">
                                            {(provided) => (
                                                <ol
                                                    className="QualityHubTasksList-grouplessItems"
                                                    ref={provided.innerRef}
                                                    {...provided.droppableProps}
                                                >
                                                    {filteredGrouplessTasks.map((item, index) => (
                                                        <Draggable key={item.id} draggableId={item.id.toString()} index={index}>
                                                            {(provided, snapshot) => (
                                                                <li
                                                                    key={item.id}
                                                                    className="QualityHubTasksList-item"
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                >
                                                                    <QualityHubTasksListItem
                                                                        isExpanded={state.expandedId === item.id}
                                                                        item={item}
                                                                        onCreateActionClick={undefined}
                                                                        onItemClick={undefined}
                                                                        listMode={listMode}
                                                                        pendingChanges={pendingTaskChanges.tasks.get(item.id)}
                                                                        onTaskChange={onTaskChange}
                                                                        dragHandleProps={provided.dragHandleProps}
                                                                        isDragging={snapshot.isDragging}
                                                                        isAnyItemDragging={anyItemDragging}
                                                                    />
                                                                </li>
                                                            )}
                                                        </Draggable>
                                                    ))}
                                                    {provided.placeholder}
                                                </ol>
                                            )}
                                        </Droppable>
                                    </li>
                                </Conditional>
                            </ol>
                        </DragDropContext>
                    </form>
                </Conditional>
                <Conditional condition={!editMode}>
                    <ol className="QualityHubTasksList-groups">
                        {groups.map(renderGroup)}
                        <Conditional condition={filteredGrouplessTasks.length > 0}>
                            <li className="QualityHubTasksList-group">
                                <ol className="QualityHubTasksList-grouplessItems">
                                    {filteredGrouplessTasks.map((item) => (
                                        <li key={item.id} className="QualityHubTasksList-item">
                                            <QualityHubTasksListItem isExpanded={state.expandedId === item.id}
                                                                     item={item}
                                                                     onCreateActionClick={onCreateActionClick}
                                                                     onItemClick={onItemClick}
                                                                     listMode={listMode}
                                                                     pendingChanges={pendingTaskChanges.tasks.get(item.id)}
                                                                     onTaskChange={onTaskChange}
                                                                     isAnyItemDragging={anyItemDragging} />
                                        </li>
                                    ))}
                                </ol>
                            </li>
                        </Conditional>
                        <Conditional condition={showUnpublishedTasks}>
                            <li className="QualityHubTasksList-group">
                                <QualityHubTasksListGroup expandedItemId={state.expandedId}
                                                          group={unpublishedTasksGroup}
                                                          onEditGroupClick={undefined}
                                                          onCreateTaskClick={undefined}
                                                          onCreateActionClick={undefined}
                                                          onItemClick={onItemClick}
                                                          taskList={taskList}
                                                          tasks={unpublishedTasks}
                                                          listMode={listMode}
                                                          hideDisabledItems={false}
                                                          pendingTaskChanges={pendingTaskChanges.tasks}
                                                          onTaskChange={onTaskChange}
                                                          isAnyItemDragging={anyItemDragging} />
                            </li>
                        </Conditional>
                    </ol>
                </Conditional>

                <QualityHubTaskDetailsDrawer onCreateActionClick={onCreateActionClick}
                                             onDismiss={onDetailsDismissing}
                                             onEditClicked={onEditClicked}
                                             open={state.selectedTask && state.drawer === "details"}
                                             task={state.selectedTask!} />

                <QualityHubActionFormDrawer action={state.selectedAction}
                                            onDismiss={onCreateActionDismissing}
                                            open={state.selectedTask && state.drawer === "actionForm"}
                                            task={state.selectedTask!} />

                <QualityHubTaskFormDrawer listId={taskList.id}
                                          itemId={state.selectedTask?.id}
                                          onDismiss={onCreateTaskDismissing}
                                          open={state.drawer === "taskForm"}
                                          disableGroupCreation={listMode !== SpintrTypes.QualityHubTasksListMode.Creation}
                                          initialGroupId={
                                            state.selectedGroup?.id ?? groups[0]?.id}
                                          onLocalSave={
                                            listMode === SpintrTypes.QualityHubTasksListMode.Customization
                                                ? onLocalSave
                                                : undefined}
                                          localItem={
                                            listMode === SpintrTypes.QualityHubTasksListMode.Customization
                                                ? state.selectedTask
                                                : undefined} />

                <QualityHubTaskGroupFormDrawer groupId={state.selectedGroup?.id}
                                               isOpen={state.drawer === "groupForm"}
                                               localItem={
                                                listMode === SpintrTypes.QualityHubTasksListMode.Customization
                                                    ? state.selectedGroup
                                                    : undefined}
                                               onDismiss={onGroupFormDismissing}
                                               onLocalSave={
                                                listMode === SpintrTypes.QualityHubTasksListMode.Customization
                                                    ? saveGroupLocally
                                                    : undefined}
                                               taskListId={listId} />
            </div>
        </QualityHubTasksListContextProvider>
    );
}

export default QualityHubTasksList;

